import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLibrary } from '../../store/useLibrary';
import type { ConflictChoice, PatternRecord, PatternStatus } from '../../domain/types';
import { Button, Card, Modal, Segmented, useToast } from '../../components/ui';
import { Archive, Arrow, Copy, Download, Pencil, Plus, Trash, Undo, Upload } from '../../components/icons';
import { Thumbnail } from './Thumbnail';
import type { ImportAnalysis } from '../../store/model';

function download(name: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function LibraryPage() {
  const lib = useLibrary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<PatternStatus>('active');
  const [view, setView] = useState<'gallery' | 'list'>('gallery');
  const [query, setQuery] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);

  const counts = lib.counts();
  const patterns = useMemo(() => {
    let list = lib.list(status);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          Object.values(p.fields).join(' ').toLowerCase().includes(q)
      );
    }
    return list;
  }, [lib, status, query, lib.getVersion()]);

  const createNew = async () => {
    const rec = await lib.create({ name: 'Untitled pattern' });
    navigate(`/p/${rec.id}`);
  };

  const onImportFile = async (file: File) => {
    const text = await file.text();
    const res = lib.analyze(text);
    if (!res.ok) {
      toast(res.error ?? "Couldn't read that file");
      return;
    }
    const conflicts = res.items.filter((i) => i.status === 'conflict');
    if (conflicts.length === 0) {
      const r = await lib.applyImport(res.items, {});
      toast(`Imported · ${r.added} added, ${r.replaced} updated`);
      return;
    }
    setAnalysis(res);
  };

  return (
    <div className="mx-auto max-w-[1320px] px-4 pb-16">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Segmented
          value={status}
          onChange={setStatus}
          ariaLabel="Filter"
          options={[
            { value: 'active', label: `Active (${counts.active})` },
            { value: 'archived', label: `Archived (${counts.archived})` },
            { value: 'trashed', label: `Trash (${counts.trashed})` },
          ]}
        />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search patterns…"
          className="h-9 min-w-40 flex-1 rounded-xl border border-border bg-surface px-3 text-sm outline-none focus-visible:border-accent"
        />
        <Segmented
          value={view}
          onChange={setView}
          ariaLabel="View"
          options={[
            { value: 'gallery', label: 'Gallery' },
            { value: 'list', label: 'List' },
          ]}
        />
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onImportFile(f);
            e.target.value = '';
          }}
        />
        <Button variant="ghost" onClick={() => fileRef.current?.click()}>
          <Upload /> Import
        </Button>
        <Button variant="ghost" onClick={() => download('skein-library.skein.json', lib.exportLibrary())}>
          <Download /> Export
        </Button>
        <Button variant="primary" onClick={createNew}>
          <Plus /> New pattern
        </Button>
      </div>

      {patterns.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-20 text-center text-text-3">
          <p className="font-display text-xl text-text-2">{query ? 'No matches' : 'Nothing here yet'}</p>
          <p className="mt-1 text-sm">
            {status === 'trashed'
              ? 'Trashed patterns are kept for 365 days.'
              : status === 'archived'
                ? 'Archive patterns to tuck them away.'
                : 'Create your first pattern to get going.'}
          </p>
        </div>
      ) : view === 'gallery' ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-4">
          {patterns.map((p) => (
            <PatternCard key={p.id} record={p} navigate={navigate} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {patterns.map((p) => (
            <PatternRow key={p.id} record={p} navigate={navigate} />
          ))}
        </div>
      )}

      {analysis && <ConflictModal analysis={analysis} onClose={() => setAnalysis(null)} />}
    </div>
  );
}

function Actions({ record, navigate }: { record: PatternRecord; navigate: ReturnType<typeof useNavigate> }) {
  const lib = useLibrary();
  const { toast } = useToast();
  const p = record;
  if (p.status === 'trashed') {
    return (
      <>
        <ActBtn icon={<Undo />} label="Restore" onClick={() => void lib.restore(p.id)} />
        <ActBtn
          icon={<Trash />}
          label="Delete"
          danger
          onClick={() => {
            if (confirm(`Permanently delete “${p.name}”?`)) void lib.removeForever(p.id);
          }}
        />
      </>
    );
  }
  return (
    <>
      <ActBtn icon={<Arrow />} label="Open" onClick={() => navigate(`/p/${p.id}`)} />
      <ActBtn
        icon={<Copy />}
        label="Copy"
        onClick={async () => {
          const d = await lib.duplicate(p.id);
          toast('Duplicated');
          if (d) navigate(`/p/${d.id}`);
        }}
      />
      <ActBtn icon={<Pencil />} label="Edit" onClick={() => navigate(`/p/${p.id}`)} />
      {p.status === 'archived' ? (
        <ActBtn icon={<Undo />} label="Unarchive" onClick={() => void lib.unarchive(p.id)} />
      ) : (
        <ActBtn icon={<Archive />} label="Archive" onClick={() => void lib.archive(p.id)} />
      )}
      <ActBtn icon={<Trash />} label="Trash" danger onClick={() => void lib.trash(p.id)} />
    </>
  );
}

function ActBtn({ icon, label, onClick, danger }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium hover:bg-surface-2 ${danger ? 'text-text-3 hover:text-danger' : 'text-text-3 hover:text-text'}`}
    >
      {icon}
      {label}
    </button>
  );
}

function PatternCard({ record, navigate }: { record: PatternRecord; navigate: ReturnType<typeof useNavigate> }) {
  const lib = useLibrary();
  const painted = Object.values(record.cells).filter((c) => c.color).length;
  const left = lib.trashDaysLeft(record);
  return (
    <Card className="flex flex-col overflow-hidden">
      <button
        className="relative flex aspect-square items-center justify-center border-b border-border bg-surface-2 p-3"
        onClick={() => record.status !== 'trashed' && navigate(`/p/${record.id}`)}
        title={`Open ${record.name}`}
      >
        <Thumbnail record={record} />
        {record.status !== 'active' && (
          <span className="absolute left-2 top-2 rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-semibold uppercase text-text-3">
            {record.status === 'trashed' ? `${left}d left` : 'Archived'}
          </span>
        )}
      </button>
      <div className="flex-1 p-3">
        <div className="truncate font-display text-[15px] font-semibold">{record.name}</div>
        <div className="mt-0.5 font-mono text-[10.5px] text-text-3">
          {record.cols}×{record.rows} · {painted} sts
        </div>
        {record.description && <p className="mt-1.5 line-clamp-2 text-xs text-text-3">{record.description}</p>}
      </div>
      <div className="flex flex-wrap gap-0.5 border-t border-border p-2">
        <Actions record={record} navigate={navigate} />
      </div>
    </Card>
  );
}

function PatternRow({ record, navigate }: { record: PatternRecord; navigate: ReturnType<typeof useNavigate> }) {
  const painted = Object.values(record.cells).filter((c) => c.color).length;
  return (
    <Card className="flex items-center gap-3 p-2 pr-3">
      <button className="h-16 w-16 flex-none rounded-lg border border-border bg-surface-2 p-1" onClick={() => navigate(`/p/${record.id}`)}>
        <Thumbnail record={record} size={64} />
      </button>
      <div className="min-w-0 flex-1">
        <div className="truncate font-display font-semibold">{record.name}</div>
        <div className="font-mono text-xs text-text-3">
          {record.cols}×{record.rows} · {painted} sts · {new Date(record.updatedAt).toLocaleDateString()}
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-0.5">
        <Actions record={record} navigate={navigate} />
      </div>
    </Card>
  );
}

function ConflictModal({ analysis, onClose }: { analysis: ImportAnalysis; onClose: () => void }) {
  const lib = useLibrary();
  const { toast } = useToast();
  const conflicts = analysis.items.filter((i) => i.status === 'conflict');
  const [choices, setChoices] = useState<Record<string, ConflictChoice>>(() =>
    Object.fromEntries(
      conflicts.map((i) => [i.record.id, new Date(i.record.updatedAt) > new Date(i.existing!.updatedAt) ? 'replace' : 'skip'])
    )
  );
  const apply = async (decisions: Record<string, ConflictChoice>) => {
    const r = await lib.applyImport(analysis.items, decisions);
    toast(`Import: ${r.added} added, ${r.replaced} updated, ${r.kept} copies, ${r.skipped} skipped`);
    onClose();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Resolve import conflicts"
      subtitle="Some patterns already exist on this device"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => apply({})} title="Keep whichever was edited most recently">
            Auto · newer wins
          </Button>
          <Button variant="primary" onClick={() => apply(choices)}>
            Apply choices
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-text-3">
        {analysis.items.length} pattern(s) · {analysis.items.filter((i) => i.status === 'new').length} new ·{' '}
        {conflicts.length} conflict(s).
      </p>
      <div className="flex flex-col gap-3">
        {conflicts.map((it) => {
          const incNewer = new Date(it.record.updatedAt) > new Date(it.existing!.updatedAt);
          return (
            <div key={it.record.id} className="rounded-xl border border-border p-3">
              <div className="mb-2 font-semibold">{it.record.name}</div>
              <div className="mb-2 grid grid-cols-2 gap-2 text-xs">
                <div className={`rounded-lg border p-2 ${!incNewer ? 'border-accent' : 'border-border'}`}>
                  <div className="text-text-3">On this device{!incNewer ? ' · newer' : ''}</div>
                  {new Date(it.existing!.updatedAt).toLocaleString()}
                </div>
                <div className={`rounded-lg border p-2 ${incNewer ? 'border-accent' : 'border-border'}`}>
                  <div className="text-text-3">In file{incNewer ? ' · newer' : ''}</div>
                  {new Date(it.record.updatedAt).toLocaleString()}
                </div>
              </div>
              <Segmented
                value={choices[it.record.id]}
                onChange={(v) => setChoices((c) => ({ ...c, [it.record.id]: v }))}
                options={[
                  { value: 'replace', label: 'Use file' },
                  { value: 'skip', label: 'Keep mine' },
                  { value: 'keepboth', label: 'Keep both' },
                ]}
              />
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
